import { DateTime as Luxon, DateTimeOptions, DateObject } from 'luxon';
export const DateTime = Luxon;
export type DateTime = Luxon;
type DateTimeProp = string | Date | DateTime | DateObject | number | null;

export function currentTimeZone() {
  return DateTime.local().zoneName;
}

export function currentLocale() {
  return DateTime.local().locale;
}

export function getDateTime(
  dt: DateTimeProp = null,
  timeZone: DateTimeOptions['zone'] = 'utc',
): DateTime {
  if (typeof dt === 'object' && dt instanceof DateTime) {
    return dt;
  }
  const options: DateTimeOptions = {};
  options.zone = timeZone;

  if (dt) {
    if (typeof dt === 'object') {
      if (dt instanceof Date) {
        return DateTime.fromJSDate(dt, options);
      } else {
        return DateTime.fromObject(dt);
      }
    } else if (typeof dt === 'string') {
      let iso = DateTime.fromISO(dt, options);
      if (iso.invalidReason) iso = DateTime.fromSQL(dt, options);
      return iso;
    } else if (typeof dt === 'number') {
      return DateTime.fromMillis(dt, options);
    }
  }
  return DateTime.local();
}

export function formatToISO(
  dt: DateTimeProp = null,
  timeZone?: string,
  options?: any,
): string {
  return getDateTime(dt, timeZone).toISO(options);
}

export function formatToISODate(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toISODate();
}

export function formatToDateTime(
  dt: DateTimeProp = null,
  timeZone?: string,
  locale?: string,
) {
  return getDateTime(dt, timeZone)
    .setLocale(locale || currentLocale())
    .toLocaleString(DateTime.DATETIME_MED);
}

export function formatToYMD(
  dt: DateTimeProp = null,
  timeZone?: string,
  options?: { delimiter?: string; noDelimiter?: boolean },
) {
  if (options?.noDelimiter) {
    return getDateTime(dt, timeZone).toFormat(`yyyyMMdd`);
  }
  const delimiter = options && options.delimiter ? options.delimiter : '-';
  return getDateTime(dt, timeZone).toFormat(`yyyy${delimiter}MM${delimiter}dd`);
}

export function formatToEXDATE(dt: DateTimeProp = null) {
  return getDateTime(dt).toFormat(`yyyyMMdd'T'HHmmss`);
}

export function formatToYear(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toFormat('yyyy');
}

export function formatToMonth(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toFormat('M');
}

export function formatToDay(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toFormat('d');
}

export function formatToHour(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toFormat('HH');
}

export function formatToMinute(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toFormat('mm');
}

export function formatToWeekDay(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toFormat('EEE');
}

export function formatToShortWeekDay(
  dt: DateTimeProp = null,
  timeZone?: string,
) {
  return getDateTime(dt, timeZone).toFormat('EEEEE');
}

export function formatToSQL(dt: DateTimeProp = null, timeZone?: string) {
  return dateTimeToSQL(getDateTime(dt, timeZone));
}

export function dateTimeToSQL(dateTime: DateTime) {
  return dateTime.toFormat('yyyy-MM-dd HH:mm:ss');
}

export function formatToDate(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toLocaleString(
    DateTime.DATE_MED_WITH_WEEKDAY,
  );
}

export function formatToDateShort(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toFormat('M/d');
}

export function formatToDateWithoutWeekday(
  dt: DateTimeProp = null,
  timeZone?: string,
) {
  return getDateTime(dt, timeZone).toLocaleString(DateTime.DATE_MED);
}

export function formatToTime(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toFormat('HH:mm');
}

export function formatToTimeShort(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toFormat('H:mm');
}

export function formatToMills(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toMillis();
}

export function formatToSeconds(dt: DateTimeProp = null, timeZone?: string) {
  return getDateTime(dt, timeZone).toSeconds();
}
